import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useQuery } from '@tanstack/react-query';
import { sortBy } from 'lodash';
import { useMemo } from 'react';

import { useAuthState } from '~/auth';
import { LeagueType, User } from '~/server/src/dtos';

import { getFn, RequestError } from './api';

export const toplists = createQueryKeys('toplists', {
  monthly: (tableLeague: string, month: number, year: number) => ({
    queryKey: [tableLeague, month, year],
  }),
  global: null,
});

export const getTable = (tableLeague: LeagueType) => {
  switch (tableLeague) {
    case LeagueType.Bronze:
      return 'toplistBronze';
    case LeagueType.Silver:
      return 'toplistSilver';
    case LeagueType.Gold:
      return 'toplistGold';
    default:
      return 'toplistBronze';
  }
};

export type TypeFilter = 'horses' | 'users';
export type GlobalToplistRankedUser = User & { globalToplistRank: number };
export type GlobalToplistPage = {
  items: User[];
  total: number;
  page: number;
  pageSize: number;
};

export function selectGlobalToplistItems(page: GlobalToplistPage): GlobalToplistRankedUser[] {
  return page.items.map((user, index) => ({
    ...user,
    username: user.username.length ? user.username : '---',
    globalToplistRank: index + 1,
  }));
}

export const useUserTable = (
  tableLeague: LeagueType,
  month: number,
  year: number,
  options?: Omit<Parameters<typeof useQuery<User[], RequestError>>[0], 'queryKey' | 'queryFn'>,
) => {
  const { isAuthorized, state: authState } = useAuthState();

  const { data = [], isLoading } = useQuery<User[], RequestError>({
    ...toplists.monthly(tableLeague, month, year),
    queryFn: ({ signal }) =>
      getFn(`/${getTable(tableLeague)}?m=${month}&y=${year}`, undefined, undefined, { signal }),
    ...options,
    enabled: authState !== 'initializing' && isAuthorized && (options?.enabled ?? true),
  });
  const sortedData = useMemo<User[]>(
    () =>
      sortBy(
        data.map(({ username, ...userProps }) => ({
          ...userProps,
          username: username.length ? username : '---',
        })),
        [
          (user) => (user.toplistRank >= 0 ? 0 : Number.MAX_SAFE_INTEGER),
          (user) => user.toplistRank,
          (user) => user.userId,
        ],
      ),
    [data],
  );
  return { data: sortedData, isLoading };
};

export const useUserGlobalToplist = (
  options?: Omit<
    Parameters<typeof useQuery<GlobalToplistPage, RequestError, GlobalToplistRankedUser[]>>[0],
    'queryKey' | 'queryFn' | 'select'
  >,
) => {
  const { isAuthorized, state: authState } = useAuthState();

  const { data = [], isLoading } = useQuery<GlobalToplistPage, RequestError, GlobalToplistRankedUser[]>({
    ...toplists.global,
    queryFn: ({ signal }) => getFn('/toplistGlobal', undefined, undefined, { signal }),
    ...options,
    enabled: authState !== 'initializing' && isAuthorized && (options?.enabled ?? true),
    select: selectGlobalToplistItems,
  });
  return { data, isLoading };
};
